import { forwardRef } from 'react';

const About = forwardRef<HTMLElement>((props, ref) => {
  return (
    <section ref={ref} className="bg-[#F8F6F3] py-[220px] md:py-[220px] py-[100px] opacity-0 transition-all duration-[800ms]" id="about">
      <div className="max-w-[1600px] mx-auto px-6 md:px-[80px]">

        {/* Top label */}
        <p className="text-[#3A3D42] text-[10px] uppercase tracking-[0.45em] font-sans mb-[80px] md:mb-[80px] mb-[40px]">
          The Anteroom Mandate
        </p>

        {/* Main grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-[40px] md:gap-[40px] gap-[32px] items-start mb-[160px] md:mb-[160px] mb-[80px]">

          {/* Left — massive headline */}
          <div className="md:col-span-6">
            <h2
              className="font-serif text-black leading-[0.92] tracking-[-0.02em]"
              style={{
                fontSize: 'clamp(36px, 6vw, 88px)',
                fontFamily: '"Playfair Display", Georgia, serif',
              }}
            >
              The Anteroom<span style={{ color: '#C9A96E' }}>,</span><br />
              <em style={{ fontStyle: 'italic', color: '#3A3D42' }}>where power</em><br />
              is prepared.
            </h2>
            <div className="flex items-center gap-4 mt-[36px]">
              <div className="w-[48px] h-[1px] bg-[#C9A96E]" />
              <span className="text-[#C9A96E] font-sans uppercase text-[9px]" style={{ letterSpacing: '0.35em' }}>
                Sovereign Advisory
              </span>
            </div>
          </div>

          {/* Right — body copy */}
          <div className="md:col-span-5 md:col-start-8 md:pt-[16px]">
            <p className="text-[#4A4A4A] text-[16px] md:text-[18px] font-sans leading-[1.85] tracking-[0.015em] mb-[36px] md:mb-[36px] mb-[24px]">
              Every great leader who has commanded a summit, moved a parliament, or shaped a generation's narrative — they did not arrive unprepared. They passed through an anteroom first.
            </p>
            <p className="text-[#4A4A4A] text-[16px] md:text-[18px] font-sans leading-[1.85] tracking-[0.015em] mb-[36px] md:mb-[36px] mb-[24px]">
              Anteroom Sovereign Advisory is Africa's premier sovereign advisory firm. We exist for one purpose: to ensure that when Africa's most consequential leaders step into the main room of global power, they are not just present — they are <strong className="text-black font-normal" style={{ borderBottom: '1px solid #C9A96E' }}>undeniable</strong>.
            </p>
            <p className="text-[#4A4A4A] text-[16px] md:text-[18px] font-sans leading-[1.85] tracking-[0.015em]">
              Operating from Nairobi, New York, and Washington DC, we serve heads of state, ministers, members of parliament, and business elites who understand that in the arena of global affairs, preparation is not optional — it is the difference between legacy and obscurity.
            </p>
          </div>
        </div>

        {/* Full-width image with overlay text */}
        <div className="relative overflow-hidden h-[400px] md:h-[560px]">
          <img
            src="https://public.readdy.ai/ai/img_res/2a2321445664a4de605ce423d0d80864.jpg"
            alt="Summit Hall"
            className="w-full h-full object-cover object-center"
            style={{ filter: 'saturate(0.6) contrast(1.1)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-transparent" />

          {/* Overlay content */}
          <div className="absolute inset-0 flex items-center">
            <div className="ml-6 md:ml-[100px] max-w-[90%] md:max-w-[600px]">
              <p
                className="text-white font-serif mb-[32px] md:mb-[32px] mb-[20px] leading-[1.2]"
                style={{
                  fontSize: 'clamp(20px, 3.5vw, 48px)',
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontStyle: 'italic',
                }}
              >
                "Go through Anteroom, and you will be transformed and ready for the main room of power."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-[40px] h-[1px] bg-[#C9A96E]" />
                <span className="text-[#C9A96E] font-sans uppercase text-[8px] md:text-[9px]" style={{ letterSpacing: '0.3em' }}>
                  Nairobi &nbsp;·&nbsp; New York &nbsp;·&nbsp; Washington DC
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Three pillars of philosophy */}
        <div className="flex flex-col md:grid md:grid-cols-3 md:divide-x divide-[#3A3D42]/20 mt-[120px] md:mt-[120px] mt-[60px] gap-[48px] md:gap-0">
          {[
            {
              label: 'Discretion',
              text: 'Every engagement is conducted under absolute confidentiality. The leaders we serve trust us with their most sensitive preparation.',
            },
            {
              label: 'Authority',
              text: 'We do not teach performance. We cultivate genuine command — the kind that comes from deep preparation and sovereign self-knowledge.',
            },
            {
              label: 'Precision',
              text: 'Every word, every posture, every message is calibrated. Nothing is left to chance when the stakes are national and the audience is global.',
            },
          ].map((item, i) => (
            <div key={i} className="md:px-[60px] md:first:pl-0 md:last:pr-0 border-t md:border-t-0 border-[#3A3D42]/20 pt-[32px] md:pt-0 first:border-t-0 first:pt-0">
              <div className="w-[32px] h-[1px] bg-[#C9A96E] mb-[32px]" />
              <h3
                className="font-serif text-black mb-[20px]"
                style={{
                  fontSize: 'clamp(22px, 4vw, 28px)',
                  fontFamily: '"Playfair Display", Georgia, serif',
                  letterSpacing: '0.02em',
                }}
              >
                {item.label}
              </h3>
              <p className="text-[#6B6B6B] text-[14px] md:text-[15px] font-sans leading-[1.8] tracking-[0.015em]">
                {item.text}
              </p>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
});

About.displayName = 'About';

export default About;
